import type { DecisionItem, Prefs, WheelList } from "../types";

const K = {
  device: "dither.deviceId",
  history: "dither.history",
  favs: "dither.favourites",
  prefs: "dither.prefs",
  hosts: "dither.hostTokens",
  lists: "dither.wheelLists",
};

function read<T>(key: string, fallback: T): T {
  try {
    const v = localStorage.getItem(key);
    return v ? (JSON.parse(v) as T) : fallback;
  } catch {
    return fallback;
  }
}

function write(key: string, value: unknown) {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch {
    /* storage full or blocked */
  }
}

export function uid(): string {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) return crypto.randomUUID();
  return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

export function getDeviceId(): string {
  let id = read<string>(K.device, "");
  if (!id) {
    id = uid();
    write(K.device, id);
  }
  return id;
}

export const DEFAULT_PREFS: Prefs = { reduced_motion: false, favourite_categories: [], dislikes: [], sound_effects: false };

export const local = {
  history: () => read<DecisionItem[]>(K.history, []),
  setHistory: (v: DecisionItem[]) => write(K.history, v.slice(0, 200)),
  favourites: () => read<DecisionItem[]>(K.favs, []),
  setFavourites: (v: DecisionItem[]) => write(K.favs, v.slice(0, 300)),
  lists: () => read<WheelList[]>(K.lists, []),
  setLists: (v: WheelList[]) => write(K.lists, v.slice(0, 100)),
  prefs: () => ({ ...DEFAULT_PREFS, ...read<Partial<Prefs>>(K.prefs, {}) }),
  setPrefs: (v: Prefs) => write(K.prefs, v),
  hostToken: (groupId: string) => read<Record<string, string>>(K.hosts, {})[groupId],
  setHostToken: (groupId: string, token: string) => write(K.hosts, { ...read(K.hosts, {}), [groupId]: token }),
  customOptions: (categoryId: string) => read<string[]>(`dither.custom.${categoryId}`, []),
  setCustomOptions: (categoryId: string, v: string[]) => write(`dither.custom.${categoryId}`, v),
};
